/**
 * Gen 5 data access layer wrapping @pkmn/data.
 * Provides a narrow interface for species, moves, abilities, items, natures,
 * learnsets, and stat calculations.
 */

import { Generations, type Generation } from "@pkmn/data";
import { Dex } from "@pkmn/sim";
import { GENERATION } from "../config/constants";

/** The Generation 5 data context. */
const gen: Generation = new Generations(Dex as any).get(GENERATION);

/** Re-export the generation for engine use. */
export { gen };

/** Check if a species exists in Gen 5. */
export function speciesExists(name: string): boolean {
  const s = gen.species.get(name);
  return !!s && s.exists && s.num <= 649;
}

/** Get all Gen 5 species names. */
export function getAllSpecies(): string[] {
  const names: string[] = [];
  for (const s of gen.species) {
    if (s.exists && s.num <= 649) {
      names.push(s.name);
    }
  }
  return names;
}

/** Get abilities for a species in Gen 5. */
export function getSpeciesAbilities(name: string): string[] {
  const s = gen.species.get(name);
  if (!s) return [];
  const abilities: string[] = [];
  if (s.abilities[0]) abilities.push(s.abilities[0]);
  if (s.abilities[1]) abilities.push(s.abilities[1]);
  if (s.abilities.H) abilities.push(s.abilities.H);
  return abilities;
}

/** Get types for a species. */
export function getSpeciesTypes(name: string): string[] {
  const s = gen.species.get(name);
  if (!s) return [];
  return [...s.types];
}

/** Get base stats for a species. */
export function getBaseStats(name: string): Record<string, number> {
  const s = gen.species.get(name);
  if (!s) return {};
  return { ...s.baseStats };
}

/** Check if a move exists and is available in Gen 5. */
export function moveExists(name: string): boolean {
  const m = gen.moves.get(name);
  return !!m && m.exists && (m.gen ?? 0) <= GENERATION;
}

/** Get all Gen 5 move names. */
export function getAllMoves(): string[] {
  const names: string[] = [];
  for (const m of gen.moves) {
    if (m.exists && (m.gen ?? 0) <= GENERATION) {
      names.push(m.name);
    }
  }
  return names;
}

/** Check if an ability exists in Gen 5. */
export function abilityExists(name: string): boolean {
  const a = gen.abilities.get(name);
  return !!a && a.exists && (a.gen ?? 0) <= GENERATION;
}

/** Get all Gen 5 ability names. */
export function getAllAbilities(): string[] {
  const names: string[] = [];
  for (const a of gen.abilities) {
    if (a.exists && (a.gen ?? 0) <= GENERATION) {
      names.push(a.name);
    }
  }
  return names;
}

/** Check if an item exists in Gen 5. */
export function itemExists(name: string): boolean {
  const i = gen.items.get(name);
  return !!i && i.exists && (i.gen ?? 0) <= GENERATION;
}

/** Get all Gen 5 item names. */
export function getAllItems(): string[] {
  const names: string[] = [];
  for (const i of gen.items) {
    if (i.exists && (i.gen ?? 0) <= GENERATION) {
      names.push(i.name);
    }
  }
  return names;
}

/** Get all Gen 5 nature names. */
export function getAllNatures(): string[] {
  const names: string[] = [];
  for (const n of gen.natures) {
    if (n.exists) names.push(n.name);
  }
  return names;
}

/** Get a nature by name. */
export function getNature(name: string) {
  return gen.natures.get(name);
}

/** Get the learnset for a species (async due to @pkmn/data). */
export async function getLearnset(
  name: string,
): Promise<Record<string, string[]> | undefined> {
  return gen.learnsets.learnable(name);
}

/** Check if a species can learn a move in Gen 5. */
export async function canLearn(species: string, move: string): Promise<boolean> {
  return gen.learnsets.canLearn(species, move);
}

/** Calculate a stat for a given base/iv/ev/level/nature. */
export function calcStat(
  stat: "hp" | "atk" | "def" | "spa" | "spd" | "spe",
  base: number,
  iv: number,
  ev: number,
  level: number,
  natureName?: string,
): number {
  const nature = natureName ? gen.natures.get(natureName) : undefined;
  return gen.stats.calc(stat, base, iv, ev, level, nature);
}
